'use client';

import React, {
  createContext,
  useContext,
  useMemo,
  ReactNode,
  useCallback,
  useState,
  useEffect,
} from 'react';
import {
  useConnection,
  useWallet,
} from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import {
  Connection,
  LAMPORTS_PER_SOL,
  PublicKey,
  SystemProgram,
  Transaction,
} from '@solana/web3.js';
import {
  getAssociatedTokenAddress,
  getMint,
  createTransferInstruction,
  createAssociatedTokenAccountInstruction,
} from '@solana/spl-token';
import { clearGameRootEntryAllowed } from '../utils/sessionExpiration';

type SendSplArgs = {
  mint: string;            // SPL mint address (e.g. USDT SPL)
  toOwner: string;         // recipient owner (wallet) pubkey
  amount: number;          // human amount (e.g. 1.5 USDT)
  decimals?: number;       // optional override, auto-read from chain if omitted
  payer?: PublicKey;       // optional custom payer (defaults to connected wallet)
};

const SESSION_WALLET_KEY = 'eclipsera_session_wallet';

/** Must match backend `utils/virtualWalletAddress.js` PREFIX */
const VIRTUAL_WALLET_PREFIX = 'eclip_vw_';

export function isVirtualGameWalletAddress(addr: string | null): boolean {
  return !!addr && addr.startsWith(VIRTUAL_WALLET_PREFIX);
}

/**
 * Email / Google game identity must survive browser restarts for the life of the HttpOnly session
 * (cookies already last 24h on the server). sessionStorage cleared on close — use localStorage.
 */
function readSessionWalletFromStorage(): string | null {
  try {
    const fromLs = localStorage.getItem(SESSION_WALLET_KEY);
    if (fromLs) return fromLs;
    const fromSs = sessionStorage.getItem(SESSION_WALLET_KEY);
    if (fromSs) {
      localStorage.setItem(SESSION_WALLET_KEY, fromSs);
      sessionStorage.removeItem(SESSION_WALLET_KEY);
      return fromSs;
    }
    return null;
  } catch {
    return null;
  }
}

/** Persist game identity when logged in with email (virtual wallet) until a real wallet is linked. */
export function setGameSessionWalletAddress(addr: string | null) {
  try {
    if (addr) {
      localStorage.setItem(SESSION_WALLET_KEY, addr);
      sessionStorage.removeItem(SESSION_WALLET_KEY);
    } else {
      clearGameRootEntryAllowed();
      localStorage.removeItem(SESSION_WALLET_KEY);
      sessionStorage.removeItem(SESSION_WALLET_KEY);
    }
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(new CustomEvent('eclipsera-session-wallet'));
  } catch {
    /* ignore */
  }
}

/** Used on app load to refresh HttpOnly cookies when user returns after closing the browser (email / virtual wallet session). */
export function hasPersistedEmailSessionWallet(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw =
      localStorage.getItem(SESSION_WALLET_KEY) ||
      sessionStorage.getItem(SESSION_WALLET_KEY);
    return !!raw && isVirtualGameWalletAddress(raw);
  } catch {
    return false;
  }
}

type Web3Helpers = {
  connected: boolean;
  connecting: boolean;
  publicKey: PublicKey | null;
  connection: Connection;
  address: string | null;

  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  openWalletModal: () => void;
  setSessionWalletFromLogin: (walletAddress: string | null) => void;

  // funds
  sendSol: (to: string, amountSol: number) => Promise<string>;
  sendSpl: (args: SendSplArgs) => Promise<string>;

  // balances
  getNativeBalance: (owner?: PublicKey) => Promise<number>; // in SOL
  getSplBalance: (mint: string, owner?: PublicKey) => Promise<number>; // human units
};

const Web3ContextInternal = createContext<Web3Helpers | null>(null);

export function Web3ContextProvider({ children }: { children: ReactNode }) {
  const { connection } = useConnection();
  const wallet = useWallet();
  const { setVisible } = useWalletModal();

  const [busy, setBusy] = useState(false);
  const [sessionWalletOverride, setSessionWalletOverride] = useState<string | null>(() =>
    typeof window !== 'undefined' ? readSessionWalletFromStorage() : null,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const sync = () => setSessionWalletOverride(readSessionWalletFromStorage());
    window.addEventListener('eclipsera-session-wallet', sync);
    return () => window.removeEventListener('eclipsera-session-wallet', sync);
  }, []);

  const setSessionWalletFromLogin = useCallback((walletAddress: string | null) => {
    setGameSessionWalletAddress(walletAddress);
    setSessionWalletOverride(walletAddress);
  }, []);

  // API identity: use Phantom (etc.) when it is the account, or session when email-only.
  // If user logged in with email (virtual eclip_vw_… id) and connects Phantom before "link wallet",
  // JWT + middleware expect the virtual id — sending Phantom's pubkey causes 403 ownership errors.
  const address = useMemo(() => {
    const pk = wallet.publicKey?.toBase58() ?? null;
    const session = sessionWalletOverride;
    if (pk && session && isVirtualGameWalletAddress(session) && session !== pk) {
      return session;
    }
    return pk ?? session ?? null;
  }, [wallet.publicKey, sessionWalletOverride]);

  const connected = useMemo(
    () => wallet.connected || (!!sessionWalletOverride && !wallet.publicKey),
    [wallet.connected, wallet.publicKey, sessionWalletOverride],
  );

  const openWalletModal = useCallback(() => setVisible(true), [setVisible]);

  const connect = useCallback(async () => {
    if (!wallet.connected && !wallet.connecting) {
      try {
        await wallet.connect();
      } catch (e) {
        // swallow modal close / user cancel
      }
    }
  }, [wallet]);

  const disconnect = useCallback(async () => {
    try {
      await wallet.disconnect();
    } catch {}
  }, [wallet]);

  const toBN = (amount: number, decimals: number) =>
    BigInt(Math.round(amount * 10 ** decimals));

  const ensureRecipientAtaIx = async ({
    mintPk,
    ownerPk,
    payerPk,
  }: {
    mintPk: PublicKey;
    ownerPk: PublicKey;
    payerPk: PublicKey;
  }) => {
    const ata = await getAssociatedTokenAddress(mintPk, ownerPk);
    const info = await connection.getAccountInfo(ata);
    if (!info) {
      return createAssociatedTokenAccountInstruction(
        payerPk, // payer
        ata,     // ata
        ownerPk, // owner of token account
        mintPk
      );
    }
    return null;
  };

  const sendSol = useCallback(
    async (to: string, amountSol: number) => {
      if (!wallet.publicKey) {
        throw new Error('Connect a wallet first');
      }
      
      setBusy(true);
      
      try {
        const toPk = new PublicKey(to);
        const tx = new Transaction().add(
          SystemProgram.transfer({
            fromPubkey: wallet.publicKey,
            toPubkey: toPk,
            lamports: Math.round(amountSol * LAMPORTS_PER_SOL),
          })
        );
        
        const { blockhash, lastValidBlockHeight } =
          await connection.getLatestBlockhash('confirmed');
        tx.feePayer = wallet.publicKey;
        tx.recentBlockhash = blockhash;

        let sig: string;
        
        // Try signTransaction first (works better on mobile), then send raw
        if (wallet.signTransaction) {
          const signedTx = await wallet.signTransaction(tx);
          sig = await connection.sendRawTransaction(signedTx.serialize(), {
            skipPreflight: false,
            preflightCommitment: 'confirmed',
          });
        } else if (wallet.sendTransaction) {
          // Fallback to sendTransaction if signTransaction not available
          sig = await wallet.sendTransaction(tx, connection);
        } else {
          throw new Error('Wallet does not support signing transactions');
        }
        
        await connection.confirmTransaction({ signature: sig, blockhash, lastValidBlockHeight }, 'confirmed');
        
        return sig;
      } catch (error: any) {
        // Provide user-friendly error messages
        if (error.message?.includes('User rejected') || error.message?.includes('User canceled')) {
          throw new Error('Transaction cancelled by user');
        } else if (error.message?.includes('insufficient')) {
          throw new Error('Insufficient SOL balance');
        } else {
          throw new Error(error.message || 'Transaction failed. Please try again.');
        }
      } finally {
        setBusy(false);
      }
    },
    [wallet.publicKey, wallet.signTransaction, wallet.sendTransaction, connection]
  );

  const sendSpl = useCallback(
    async ({ mint, toOwner, amount, decimals, payer }: SendSplArgs) => {
      if (!wallet.publicKey) {
        throw new Error('Connect a wallet first');
      }
      
      setBusy(true);
      
      try {
        const mintPk = new PublicKey(mint);
        const toOwnerPk = new PublicKey(toOwner);
        const fromOwnerPk = payer ?? wallet.publicKey;

        // infer decimals from chain if not provided
        const mInfo = decimals == null ? await getMint(connection, mintPk) : null;
        const tokenDecimals = decimals ?? mInfo!.decimals;

        // derive ATAs
        const fromATA = await getAssociatedTokenAddress(mintPk, fromOwnerPk);
        const toATA = await getAssociatedTokenAddress(mintPk, toOwnerPk);

        // Build ixs
        const ixs = [];

        // Ensure recipient ATA exists
        const maybeCreateToAtaIx = await ensureRecipientAtaIx({
          mintPk,
          ownerPk: toOwnerPk,
          payerPk: fromOwnerPk,
        });
        if (maybeCreateToAtaIx) ixs.push(maybeCreateToAtaIx);

        // Transfer
        ixs.push(
          createTransferInstruction(
            fromATA,
            toATA,
            fromOwnerPk,
            Number(toBN(amount, tokenDecimals))
          )
        );

        const tx = new Transaction().add(...ixs);
        
        const { blockhash, lastValidBlockHeight } =
          await connection.getLatestBlockhash('confirmed');
        tx.feePayer = fromOwnerPk;
        tx.recentBlockhash = blockhash;

        let sig: string;
        
        // Try signTransaction first (works better on mobile), then send raw
        if (wallet.signTransaction) {
          const signedTx = await wallet.signTransaction(tx);
          sig = await connection.sendRawTransaction(signedTx.serialize(), {
            skipPreflight: false,
            preflightCommitment: 'confirmed',
          });
        } else if (wallet.sendTransaction) {
          // Fallback to sendTransaction if signTransaction not available
          sig = await wallet.sendTransaction(tx, connection);
        } else {
          throw new Error('Wallet does not support signing transactions');
        }
        
        await connection.confirmTransaction({ signature: sig, blockhash, lastValidBlockHeight }, 'confirmed');
        
        return sig;
      } catch (error: any) {
        // Provide user-friendly error messages
        if (error.message?.includes('User rejected') || error.message?.includes('User canceled')) {
          throw new Error('Transaction cancelled by user');
        } else if (error.message?.includes('insufficient')) {
          throw new Error('Insufficient balance or SOL for transaction fees');
        } else {
          throw new Error(error.message || 'Transaction failed. Please try again.');
        }
      } finally {
        setBusy(false);
      }
    },
    [wallet.publicKey, wallet.signTransaction, wallet.sendTransaction, connection]
  );

  const getNativeBalance = useCallback(
    async (owner?: PublicKey) => {
      const pk = owner ?? wallet.publicKey;
      if (!pk) throw new Error('No wallet connected');
      const lamports = await connection.getBalance(pk);
      return lamports / LAMPORTS_PER_SOL;
    },
    [connection, wallet.publicKey]
  );

  const getSplBalance = useCallback(
    async (mint: string, owner?: PublicKey) => {
      const ownerPk = owner ?? wallet.publicKey;
      if (!ownerPk) throw new Error('No wallet connected');
      const mintPk = new PublicKey(mint);
      const ata = await getAssociatedTokenAddress(mintPk, ownerPk);
      const [accInfo, mintInfo] = await Promise.all([
        connection.getTokenAccountBalance(ata).catch(() => null),
        getMint(connection, mintPk),
      ]);
      if (!accInfo) return 0;
      const uiAmount = Number(accInfo.value.amount) / 10 ** mintInfo.decimals;
      return uiAmount;
    },
    [connection, wallet.publicKey]
  );

  const value = useMemo<Web3Helpers>(
    () => ({
      connected,
      connecting: wallet.connecting || busy,
      publicKey: wallet.publicKey,
      connection,
      address,
      connect,
      disconnect,
      openWalletModal,
      setSessionWalletFromLogin,
      sendSol,
      sendSpl,
      getNativeBalance,
      getSplBalance,
    }),
    [
      connected,
      wallet.connecting,
      wallet.publicKey,
      connection,
      address,
      connect,
      disconnect,
      openWalletModal,
      setSessionWalletFromLogin,
      sendSol,
      sendSpl,
      getNativeBalance,
      getSplBalance,
      busy,
    ]
  );

  return <Web3ContextInternal.Provider value={value}>{children}</Web3ContextInternal.Provider>;
}

export function useWeb3Context() {
  const ctx = useContext(Web3ContextInternal);
  if (!ctx) throw new Error('useWeb3Context must be used within <Web3ContextProvider/>');
  return ctx;
}
